import type { ComputedRef } from 'vue'

import { useChangelogStore } from '@entities/changelog'

interface ChangelogDetailRow {
  label: string
  value: string
}

interface ModalChangelogDetailReturn {
  refIsOpen: ComputedRef<boolean>
  refTitle: ComputedRef<string>
  refRows: ComputedRef<ChangelogDetailRow[]>
  closeModal: () => void
}

/** Arrays and objects are stored JSON-encoded; show them pretty-printed like the legacy modal did. */
const formatValue = (value: string): string => {
  if (!value.trim().startsWith('{')) return value

  try {
    return JSON.stringify(JSON.parse(value), null, 2)
  } catch {
    return value
  }
}

export const useModalChangelogDetail = (): ModalChangelogDetailReturn => {
  const store = useChangelogStore()

  const refIsOpen = computed<boolean>(() => !!store.getDetailItem)
  const refTitle = computed<string>(() => `Detailed changelog #${store.getDetailItem?.id ?? ''}`)

  const refRows = computed<ChangelogDetailRow[]>(() => {
    const item = store.getDetailItem
    if (!item) return []

    const rows: ChangelogDetailRow[] = [
      { label: 'Field', value: item.field },
      { label: 'Action', value: item.action },
      { label: 'Value', value: item.value ?? '' },
      { label: 'Old Value', value: item.old_value ?? '' },
    ]

    return rows
      .filter(row => !!row.value)
      .map(row => ({ ...row, value: formatValue(row.value) }))
  })

  const closeModal = (): void => {
    store.setDetailItem(null)
  }

  return {
    refIsOpen,
    refTitle,
    refRows,
    closeModal,
  }
}
